import { createFileRoute, Link } from "@tanstack/react-router";

import { hreflangLinks, absoluteUrl } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { POSTS, TOPICS, AGE_GROUPS, SERIES } from "@/lib/montessori-koulu-posts";
import { ArticleCard } from "@/components/hub/ArticleCard";
import { HubExplorer } from "@/components/hub/HubExplorer";
import { CATEGORY_HERO } from "@/lib/article-heroes";

const HUB_OG_IMAGE = absoluteUrl(CATEGORY_HERO.perusteet);
const HUB_TITLE = "Montessori-koulu — käytännön oppaat vanhemmille";
const HUB_DESC = "Käytännöllisiä Montessori-ideoita suomalaisen lapsiperheen arkeen. Ilmainen tietopankki vanhemmille.";

export const Route = createFileRoute("/montessori-koulu/")({
  head: () => ({
    meta: [
      { title: "Montessori-koulu — käytännön oppaat vanhemmille | Montessori24" },
      { name: "description", content: "Ilmainen tietopankki vanhemmille: käytännöllisiä Montessori-ideoita suomalaisen lapsiperheen arkeen. Oppaat kotiin, liikkumiseen, arjen taitoihin ja tuotteen valintaan — ei vaadi ostamista." },
      { property: "og:title", content: HUB_TITLE },
      { property: "og:description", content: HUB_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://montessori24.fi/montessori-koulu" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: HUB_OG_IMAGE },
      { property: "og:image:alt", content: HUB_TITLE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: HUB_TITLE },
      { name: "twitter:description", content: HUB_DESC },
      { name: "twitter:image", content: HUB_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://montessori24.fi/montessori-koulu" }, ...hreflangLinks("https://montessori24.fi/montessori-koulu")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Montessori-koulu",
          url: "https://montessori24.fi/montessori-koulu",
          inLanguage: "fi-FI",
          publisher: { "@type": "Organization", name: "Montessori24" },
        }),
      },
    ],
  }),
  component: MontessoriKoulu,
});

const CATEGORY_LINKS: ReadonlyArray<{ to: "/oppimistorni" | "/pikler-kolmio" | "/kiipeilyteline" | "/lasten-vaaterekki"; label: string; sub: string }> = [
  { to: "/oppimistorni", label: "Oppimistornit", sub: "12 kk – 5 v" },
  { to: "/pikler-kolmio", label: "Pikler-kolmiot", sub: "Ikä vaihtelee mallista" },
  { to: "/kiipeilyteline", label: "Kiipeilytelineet", sub: "Ikä vaihtelee mallista" },
  { to: "/lasten-vaaterekki", label: "Vaaterekit", sub: "18 kk +" },
];

function MontessoriKoulu() {
  const pillar = POSTS.find((p) => p.pillar);
  const starters = POSTS.filter((p) => p.starter).slice(0, 4);
  const myths = POSTS.find((p) => p.slug === "montessori-myytit");
  const seriesPath = SERIES["montessori-kotona-perusteet"];
  const seriesPosts = seriesPath.slugs
    .map((slug) => POSTS.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  return (
    <Layout>
      {/* 1. HERO */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-28">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.28em] text-clay">Montessori-koulu</span>
            <h1 className="mt-6 font-display text-4xl leading-[1.05] text-foreground md:text-6xl text-balance">
              Pieniä oivalluksia. Suurempaa itsenäisyyttä.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Käytännöllisiä Montessori-ideoita suomalaisen lapsiperheen arkeen. Opi tukemaan lapsen
              itsenäisyyttä, motoriikkaa, keskittymistä ja osallistumista pienillä muutoksilla kotona.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href="#aloita-tasta"
                className="inline-flex items-center rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Aloita tästä
              </a>
              <a
                href="#kaikki-oppaat"
                className="inline-flex items-center rounded-full border border-border bg-card px-7 py-3.5 text-sm font-medium text-foreground transition-colors hover:border-foreground"
              >
                Selaa kaikkia oppaita
              </a>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              Ilmainen tietopankki vanhemmille – ei vaadi ostamista.
            </p>
          </div>
        </div>
      </section>

      {/* 2. UUSI MONTESSORISSA? */}
      <section id="aloita-tasta" className="scroll-mt-24 border-b border-border bg-background">
        <div className="container-prose py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Aloita tästä</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">
              Uusi Montessorissa? Aloita näistä.
            </h2>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {starters.map((p) => (
              <ArticleCard key={p.to} p={p} />
            ))}
          </div>
        </div>
      </section>

      {/* 3. FEATURED PILLAR GUIDE */}
      {pillar && (
        <section className="border-b border-border bg-cream">
          <div className="container-prose py-16 md:py-24">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Suosittu opas</span>
            <Link
              to={pillar.to}
              className="group mt-6 grid overflow-hidden rounded-3xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-[0_24px_48px_-28px_rgb(0_0_0/0.18)] md:grid-cols-2"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-sand md:aspect-auto md:min-h-[340px]">
                <img
                  src={CATEGORY_HERO[pillar.category]}
                  alt="Valoisa suomalainen koti, jossa lapsen tavarat ovat omilla paikoillaan lapsen ulottuvilla"
                  width={1280}
                  height={720}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-col justify-center gap-4 p-7 md:p-10">
                <h2 className="font-display text-3xl leading-tight text-foreground md:text-4xl text-balance">{pillar.title}</h2>
                <p className="max-w-lg text-[15px] leading-relaxed text-muted-foreground">{pillar.excerpt}</p>
                <ul className="grid gap-1.5 text-sm text-muted-foreground sm:grid-cols-2">
                  {["Mistä aloittaa", "Eteinen", "Keittiö", "Lastenhuone", "Kylpyhuone", "Lelut ja niiden kierto", "Arjen tehtävät", "Mitä ei tarvitse ostaa"].map((t) => (
                    <li key={t}>· {t}</li>
                  ))}
                </ul>
                <span className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-foreground underline underline-offset-4 group-hover:text-clay">
                  Lue koko opas · {pillar.minutes} min
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* 4. TOPIC CATEGORIES */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Aiheet</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">Valitse aihe</h2>
          </div>
          <div className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {TOPICS.map((t) => {
              const Icon = t.icon;
              const count = POSTS.filter((p) => p.topic === t.id).length;
              return (
                <a key={t.id} href="#kaikki-oppaat" className="group bg-card p-7 transition-colors hover:bg-cream">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-sage/10 text-sage">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <h3 className="mt-5 font-display text-xl text-foreground text-balance">{t.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.blurb}</p>
                  <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground group-hover:text-foreground">
                    {count} {count === 1 ? "opas" : "opasta"} →
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. AGE-BASED NAVIGATION */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Ikäkaudet</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">
              Mitä lapsesi harjoittelee juuri nyt?
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Ideoita tähän kehitysvaiheeseen. Lapset etenevät omaa tahtiaan, joten nämä eivät ole
              tarkistuslista siitä, mitä lapsen pitäisi osata tietyssä iässä.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AGE_GROUPS.map((g) => (
              <a
                key={g.id}
                href="#kaikki-oppaat"
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-foreground"
              >
                <p className="font-display text-2xl text-foreground">{g.label}</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{g.blurb}</p>
                <span className="mt-5 text-sm font-medium text-foreground underline underline-offset-4 group-hover:text-clay">
                  Katso tämän ikävaiheen oppaat →
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 6. ALL GUIDES — search + filters */}
      <section id="kaikki-oppaat" className="scroll-mt-24 border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <HubExplorer />
        </div>
      </section>

      {/* 7. MONTESSORI-MYYTIT */}
      {myths && (
        <section className="border-b border-border bg-cream">
          <div className="container-prose py-16 md:py-20">
            <div className="grid gap-8 rounded-3xl border border-border bg-card p-7 md:grid-cols-[1.2fr_1fr] md:p-10">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-clay">Montessori-myytit</span>
                <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">{myths.title}</h2>
                <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">{myths.excerpt}</p>
                <Link
                  to={myths.to}
                  className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay"
                >
                  Lue opas · {myths.minutes} min
                </Link>
              </div>
              <ul className="space-y-3 self-center text-sm leading-relaxed text-muted-foreground">
                <li>”Montessori tarkoittaa kalliita puuleluja”</li>
                <li>”Montessori-lapsi saa tehdä mitä haluaa”</li>
                <li>”Montessori-kodin pitää olla beige”</li>
                <li>”Montessori kieltää mielikuvitusleikit”</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 8. LEARNING PATH */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Oppimispolku</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">{seriesPath.label}</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Viisi osaa järjestyksessä. Voit lukea ne peräkkäin tai poimia sen, mikä koskee juuri nyt omaa arkeasi.
            </p>
          </div>
          <ol className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-border bg-border md:grid-cols-5">
            {seriesPosts.map((p, i) => (
              <li key={p.to} className="bg-card p-6 transition-colors hover:bg-cream">
                <Link to={p.to} className="group block h-full">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-clay">Osa {i + 1}</span>
                  <span className="mt-3 block font-display text-lg leading-snug text-foreground group-hover:underline">
                    {p.shortTitle}
                  </span>
                  <span className="mt-3 block text-xs uppercase tracking-[0.16em] text-muted-foreground">{p.minutes} min</span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 9. PRIMER — what Montessori is, kept for topical depth */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-20">
          <div className="mx-auto max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Perusteet</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl text-balance">
              Mitä Montessori-pedagogiikka on?
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
              <p>
                Montessori on italialaisen lääkärin{" "}
                <Link to="/montessori-koulu/maria-montessori-elamakerta" className="text-foreground underline underline-offset-4 hover:text-clay">Maria Montessorin</Link>{" "}
                (1870–1952) kehittämä kasvatusmenetelmä, joka perustuu lapsen omaan haluun tutkia ja
                harjoitella. Aikuinen valmistelee ympäristön, jossa lapsi voi toimia itse omaan tahtiinsa.
                Ensimmäinen Casa dei Bambini avattiin Roomassa vuonna 1907.
              </p>
              <p>
                Kotona ydin on <em>valmisteltu ympäristö</em>: lapsen ulottuvilla olevat tavarat, rajattu
                valikoima kerrallaan ja oma paikka jokaiselle esineelle. Montessori-ajattelun soveltaminen
                kotona ei vaadi suurta määrää erityisiä välineitä — monet muutokset voidaan toteuttaa
                tavallisilla kodin esineillä ja ympäristön pienillä järjestelyillä.
              </p>
              <p>
                Rinnakkaisena kehittyi unkarilaisen pediatrin{" "}
                <Link to="/montessori-koulu/emmi-pikler-elamakerta" className="text-foreground underline underline-offset-4 hover:text-clay">Emmi Piklerin</Link>{" "}
                työ, joka keskittyi pienten lasten vapaaseen liikkeeseen. Suomessa Montessori- ja
                Pikler-välineitä käytetään sekä päiväkodeissa että kodeissa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 11. PRODUCTS — kept deliberately quiet */}
      <section className="border-t border-border bg-background">
        <div className="container-prose py-14 md:py-16">
          <div className="mx-auto flex max-w-3xl flex-col items-start gap-5 rounded-3xl border border-border bg-sand/40 p-7 md:flex-row md:items-center md:justify-between md:p-9">
            <div className="min-w-0">
              <h2 className="font-display text-2xl text-foreground text-balance">Jos etsit valmista ratkaisua</h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                Oppaissa mainitut kalusteet löytyvät mallistostamme. Tarkista aina tuotekohtainen
                ikäsuositus ja valmistajan ohjeet ennen käyttöä.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/pikler-kolmio" className="inline-flex rounded-full border border-border bg-card px-5 py-2.5 text-sm text-foreground transition-colors hover:border-foreground">
                Tutustu Pikler-vaihtoehtoihin
              </Link>
              <Link to="/oppimistorni" className="inline-flex rounded-full border border-border bg-card px-5 py-2.5 text-sm text-foreground transition-colors hover:border-foreground">
                Tutustu oppimistorneihin
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 12. EDITORIAL TRANSPARENCY */}
      <section className="border-t border-border bg-cream">
        <div className="container-prose py-14 md:py-16">
          <div className="mx-auto max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Toimitus</span>
            <h2 className="mt-3 font-display text-2xl text-foreground md:text-3xl">Montessori24 toimitus</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Montessori24 kokoaa käytännöllisiä Montessori-ideoita, valmistajien tuotetietoja ja
              luotettavia lähteitä suomalaisille vanhemmille. Emme anna terveys- tai hoitosuosituksia.
              Tuotteiden turvallisuutta ja ikäsuosituksia koskevissa asioissa valmistajan omat ohjeet
              ovat aina ensisijainen tietolähde.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
